import { useSchedulerContext } from '@contexts/scheduler';
import {
  addMinutes,
  differenceInMinutes,
  isAfter,
  isBefore,
  isValid,
  parseISO,
  subMinutes,
} from 'date-fns';
import { getSegmentFromPoint } from './dnd/helpers';
import { PointerEvent } from 'react';
import { compareHours } from '@utils/dateHelpers';
import { getFirstHour, getLastHour } from '@components/scheduler/grid/helpers';
import { Schedule } from '@root/globalTypes';
import { UsersSchedules } from '@services/schedules/internal/types';
import { WorkloadBasicForeman } from '@services/workload/calendar/types';

export const useScheduler = () => {
  const {
    date,
    hours,
    modal,
    inventory,
    active: { schedule, newValues },
    segment,
    dayStart,
    setState,
  } = useSchedulerContext();
  const firstHour = getFirstHour(date, dayStart);
  const lastHour = getLastHour(hours, segment);

  const getSchedule = (
    values: { foremen: UsersSchedules[]; withoutTeam: Schedule[] },
    id?: string,
    mutate: boolean = true,
  ) => {
    const scheduleId = id || schedule?.id;
    let selectedSchedule: Schedule | undefined;

    const foremen = values.foremen.map((foreman) => ({
      ...foreman,
      schedules: foreman.schedules
        .map((el) => {
          if (el.id !== scheduleId) return el;
          selectedSchedule = el;
        })
        .filter(Boolean) as Schedule[],
    }));

    const withoutTeam = (values.withoutTeam
      ?.map((el) => {
        if (el.id !== scheduleId) return el;
        selectedSchedule = el;
      })
      .filter(Boolean) || []) as Schedule[];

    if (mutate) {
      setState((prev) => ({
        ...prev,
        data: { ...prev.data, raw: { foremen, withoutTeam } },
      }));
    }

    return selectedSchedule;
  };

  const setSchedule = (newSchedule: Schedule, foremanId: string) => {
    setState((prev) => {
      const matchedForeman = foremanId
        ? prev.data.raw.foremen.some((el) => el.id === foremanId)
        : false;

      if (!matchedForeman) {
        return {
          ...prev,
          data: {
            ...prev.data,
            raw: {
              ...prev.data.raw,
              withoutTeam: [...(prev.data.raw.withoutTeam || []), newSchedule],
            },
          },
        };
      }

      return {
        ...prev,
        data: {
          ...prev.data,
          raw: {
            ...prev.data.raw,
            foremen: prev.data.raw.foremen.map((el) => {
              if (el.id !== foremanId) return el;
              return {
                ...el,
                schedules: [...el.schedules, newSchedule],
              };
            }),
          },
        },
      };
    });
  };

  const isSegmentDisabled = (value: Date) => {
    if (!inventory) return false;
    const [startsAt, endsAt] = [parseISO(schedule.startsAt), parseISO(schedule.endsAt)];
    return isBefore(value, startsAt) || isAfter(value, endsAt) || compareHours(value, endsAt);
  };

  const updateDates = () => {
    const [startsAt, endsAt] = [parseISO(schedule.startsAt), parseISO(schedule.endsAt)];
    const { from, to } = newValues;
    if (!from || !isValid(from) || !to || !isValid(to) || !startsAt || !endsAt) return {};
    const duration = differenceInMinutes(endsAt, startsAt, { roundingMethod: 'round' });
    const fromToStart = differenceInMinutes(from, startsAt, { roundingMethod: 'round' });
    const endToFrom = differenceInMinutes(endsAt, from, { roundingMethod: 'round' });
    const newStartsAt = subMinutes(to, fromToStart);
    const newEndsAt = addMinutes(to, endToFrom);

    const lowerBound = isBefore(startsAt, firstHour) ? startsAt : firstHour;
    const upperBound = isAfter(endsAt, lastHour) ? endsAt : lastHour;

    if (isBefore(newStartsAt, lowerBound)) {
      return { startsAt: lowerBound, endsAt: addMinutes(lowerBound, duration) };
    }

    if (isAfter(newEndsAt, upperBound)) {
      return { startsAt: subMinutes(upperBound, duration), endsAt: upperBound };
    }

    return { startsAt: newStartsAt, endsAt: newEndsAt };
  };

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>, foreman: WorkloadBasicForeman) => {
    if (!modal || schedule.id) return;
    const startsAt = getSegmentFromPoint(e.clientX, e.clientY);
    setState((prev) => ({
      ...prev,
      active: {
        ...prev.active,
        schedule: {
          ...prev.active.schedule,
          id: 'new',
          foreman,
          startsAt: startsAt.toISOString(),
          endsAt: addMinutes(startsAt, segment.length).toISOString(),
        },
        newValues: {
          startsAt,
          endsAt: addMinutes(startsAt, segment.length),
        },
        fresh: false,
        mode: 'right',
        initialX: e.clientX,
        cursor: 'cursor-ew-resize',
      },
    }));
  };

  return { getSchedule, setSchedule, updateDates, handlePointerDown, isSegmentDisabled };
};
